import { useCallback, useEffect, useRef, useState } from "react";
import { SorterResult } from "antd/es/table/interface";
import {
  STATUS_CODE,
  getOffsetFromPageAndPageSize,
  openNotificationWithIcon,
} from "../utils";
import moment from "moment";
import { usePagination } from "../hooks";
import { documentService } from "../api";
import { IGetDocumentsRequest } from "../interfaces/document";
import { useDispatch, useSelector } from "react-redux";
import { RootState, setIsAllSelected, setMetros, setPageNumberForDocument, setSelectedMetros, setSelectedStatus } from "../store";

interface ColumnSort {
  field: string | null;
  sortOrder: 'ascend' | 'descend' | null;
}


export const useFetchDocuments = () => {
    const [sort, setSort] = useState<ColumnSort>({
      field: null,
      sortOrder: null
    });  const [isLoading, setIsLoading] = useState(true);
  const [documents, setDocuments] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [isAuthorized, setIsAuthorized] = useState(true);

  const defaultOverviewFromDate = moment("2023-08-01 00:00:00");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchDocumentUUID, setSearchDocumentUUID] = useState("");
  const status = useSelector((state: RootState) => state.document.selectedStatus);
  const setStatus = (value: string) => dispatch(setSelectedStatus(value));
  const [keywords, setKeywords] = useState([] as Record<string, string>[]);
  const [ isDocAckLoading, setIsDocAckLoading ] = useState<boolean>(false)
  const [ docAckData, setDocAckData ] = useState<any[]>([]);
  const [ retryData, setRetryData ] = <any> useState()
  // const [metros, setMetros] = useState<any[]>([]);
  const [metroFilters, setMetroFilters] = useState<string[]>([]);
  const isFetchingMetros = useRef(false); 

  const { fromDate, toDate } = useSelector(
    (state: RootState) => state.document.selectedDateRange
  );

  const dispatch = useDispatch();
  const paginationHook = usePagination();

  const {pageNumber, selectedMetros, metros} = useSelector(
    (state: RootState) => state.document
  );

  
  const { page, pageSize, pageSizeOptions, onPaginationChange } = usePagination();

  const onSortChange = (sorter: SorterResult<any> | SorterResult<any>[]) => {
    
    if (!Array.isArray(sorter)) {
      const newSort: ColumnSort = {
        field: sorter.field?.toString() || null,
        sortOrder: sorter.order || null,
      };
  
      if (newSort.field !== sort.field || newSort.sortOrder !== sort.sortOrder) {
        setSort(newSort);
      } 
    }
  };
  

  const getDocumentsFilterObject = useCallback(
    (projectId?: number) => {
      const fromDateWithTime = moment(fromDate)
      .format('YYYY-MM-DD HH:mm:ss');
    
    const toDateWithTime = moment(toDate)
      .format('YYYY-MM-DD HH:mm:ss');
      
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let obj: IGetDocumentsRequest = {
        limit: pageSize,
        offset: getOffsetFromPageAndPageSize(page, pageSize),
        searchTerm: searchTerm,
        searchDocumentUUID: searchDocumentUUID,
        status: status,
        projectId: projectId,
        fromDate:fromDateWithTime?.toString(),
        toDate: toDateWithTime?.toString(),
        timezone,
        metro: selectedMetros
      }
      if (sort.field && sort.sortOrder) {
        obj.sortField = sort.field;
        obj.sortType = sort.sortOrder === 'ascend' ? 'asc' : 'desc';
      }
      return { ...obj };
    },
    
    
    [
      page,
      pageSize,
      sort,
      fromDate,
      toDate,
      searchTerm,
      searchDocumentUUID,
      status,
      selectedMetros
    ]
  );

  const fetchDocuments = useCallback(async () => {    
    try {
      const {data, pagination} = await documentService.getDocuments(
        getDocumentsFilterObject()
      );
      setDocuments(data);

      setTotal(pagination.total ? pagination.total : 0);
    } catch (err: any) {
      if (err.response.status === STATUS_CODE.FORBIDDEN) {
        setIsAuthorized(false);
      } else {
        openNotificationWithIcon("", err.response.data.message, "error");
      }
    } finally {
      setIsLoading(false);
    }
  }, [getDocumentsFilterObject]);


  const fetchDocAckData = async (uuid: string)=> {
    setDocAckData([])
    setIsDocAckLoading(true);
    try{
      const { data } = await documentService.getAcknowledgementInfo(uuid);
      setDocAckData(data)
    }catch (err: any){
      openNotificationWithIcon("", err.response.data.message, "error");
    }finally {
       setIsDocAckLoading(false)
    }
  };

  const fetchRetryData = async (uuid: string) => {
    setIsDocAckLoading(true);
    try{
      const { data } = await documentService.fetchRetryAttempts(uuid);
      setRetryData(data)
    }catch (err: any){
      openNotificationWithIcon("", err.response.data.message, "error");
    }finally {
       setIsDocAckLoading(false)
    }
  }

  const getAllMetros = async () => {
    try {
      // Prevent multiple parallel fetches if metros is empty
      if (metros.length === 0 && !isFetchingMetros.current) {
        isFetchingMetros.current = true;
        console.log("🌐 Fetching metros from settings endpoint...");

        const response = await documentService.getAllMetros();
        const allMetros = response.data.map((metro: any) => metro.name);

        dispatch(setMetros(response.data));
        if (selectedMetros.length === 0) {
          dispatch(setSelectedMetros(allMetros));
        }
      }
    } catch (error) {
      console.error("Error fetching metros:", error);
    } finally {
      isFetchingMetros.current = false;
    }
  };
  
  
  return {
    paginationObj: { onPaginationChange, pageSizeOptions, page, pageSize },
    sortFilter: { sort, setSort: onSortChange },
    getDocumentsFilterObject,
    fetchDocuments,
    isDocumentsLoading: isLoading,
    setIsDocumentsLoading: setIsLoading,
    documents,
    totalDocuments: total,
    setDocuments,
    setTotalDocuments: setTotal,
    isAuthorized,
    fromDate,
    toDate,
    searchTerm,
    setSearchTerm,
    searchDocumentUUID,
    setSearchDocumentUUID,
    status,
    setStatus,
    field: keywords,
    defaultOverviewFromDate,
    isDocAckLoading, 
    fetchDocAckData,
    docAckData,
    fetchRetryData,
    retryData,
    onSortChange,
    metros,
    selectedMetros,
    setSelectedMetros,
    getAllMetros
  };
};
